import { useState, useEffect } from 'react';
import * as wasm from 'sched';
import { useDatabase } from './useDatabase';

export interface ProjectInfo {
  id: string;
  name: string;
}

export interface TaskDetails {
  id: string;
  title: string;
  description: string | null;
  estimates: wasm.WorkEstimate[];
  deadlines: wasm.Deadline[];
  remoteReference?: wasm.RemoteReference;
  complete: boolean;
  project?: ProjectInfo;
}

// Hook to access task data
export function useTask(taskId: string | undefined) {
  const { database, safeData, loading: databaseLoading } = useDatabase();
  const [task, setTask] = useState<TaskDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!database || !taskId) {
      setLoading(false);
      return;
    }

    try {
      const tasks = database.tasks();
      const taskObj = tasks[taskId];
      
      if (!taskObj) {
        setError(`Task with ID ${taskId} not found`);
        setTask(null);
      } else {
        // Get task data asynchronously
        const fetchTaskData = async () => {
          try {
            // Get remote reference
            const remoteRef = await taskObj.reference();
            
            // Get project information
            let project: ProjectInfo | undefined = undefined;
            
            try {
              // Use the safeData from useDatabase hook for project_id call
              if (safeData) {
                const projectId = await taskObj.project_id(safeData);
                
                if (projectId) {
                  const projects = database.projects();
                  const projectData = projects[projectId];
                  
                  if (projectData) {
                    project = {
                      id: projectId,
                      name: projectData.name()
                    };
                  }
                }
              }
            } catch (projectErr) {
              console.error('Error fetching project data:', projectErr);
            }
            
            // Set task with all data
            setTask({
              id: taskObj.id(),
              title: taskObj.title(),
              description: taskObj.description() || null,
              estimates: taskObj.estimates(),
              deadlines: taskObj.deadlines(),
              remoteReference: remoteRef,
              complete: taskObj.complete(),
              project
            });
            setError(null);
          } catch (err) {
            console.error('Error fetching task data:', err);
            // Still set the task with basic data
            setTask({
              id: taskObj.id(),
              title: taskObj.title(),
              description: taskObj.description() || null,
              estimates: taskObj.estimates(),
              deadlines: taskObj.deadlines(),
              remoteReference: undefined,
              complete: taskObj.complete(),
              project: undefined
            });
            setError(null);
          }
        };
        
        fetchTaskData();
      }
    } catch (err) {
      console.error('Error fetching task:', err);
      setError('Failed to fetch task details');
      setTask(null);
    } finally {
      setLoading(false);
    }
  }, [database, taskId]);

  return { task, loading: loading || databaseLoading, error };
}
